import React from "react";
import { FILTER_DEFS, filterLabel } from "./rosterTagDefs";
import { THRESHOLD_OPTIONS } from "../../../hooks/insights/insightDerive";

/**
 * The body of the Filters surface, shared by the desktop popover and the mobile
 * tray. Layout-agnostic on purpose: it renders a block and lets its host decide
 * whether that block floats or sits in flow.
 *
 * Zero-count chips are demoted into a dimmed "Empty:" row rather than taking a
 * full-size slot each — with nine possible filters and four typically matching,
 * giving the empty ones equal weight is what made the original chip row wrap to
 * three lines inside a 384px dock.
 */

/**
 * @param {Object} props
 * @param {Set<string>} props.activeIds
 * @param {(id: string|null) => void} props.onToggle - null clears everything.
 * @param {Record<string, number>} props.counts
 * @param {string[]} props.chipIds - Filters to offer, in order.
 * @param {number} props.thresholdDays
 * @param {(days: number) => void} props.onThresholdChange
 * @param {() => void} [props.onDone] - Renders the footer when provided.
 * @param {boolean} [props.showSortNote=true]
 */
export function FiltersPanel({
  activeIds,
  onToggle,
  counts,
  chipIds,
  thresholdDays,
  onThresholdChange,
  onDone,
  showSortNote = true,
}) {
  // A chip is "empty" only when nothing matches AND it is not currently on —
  // an active filter must stay where the user left it, or clearing it becomes a
  // hunt through the dimmed group.
  const live = chipIds.filter((id) => counts[id] > 0 || activeIds.has(id));
  const empty = chipIds.filter((id) => counts[id] === 0 && !activeIds.has(id));

  const showThreshold = chipIds.some(
    (id) => FILTER_DEFS[id]?.needsInsights
  );

  return (
    <div>
      <div className="flex items-center justify-between px-3.5 pt-3.5">
        <span className="text-[11px] font-bold uppercase tracking-[0.08em] text-gray-400">
          Coaching signals
        </span>
        <button
          type="button"
          onClick={() => onToggle(null)}
          className="text-xs font-semibold text-bc-accent-text hover:underline"
        >
          Clear
        </button>
      </div>

      <div className="flex flex-wrap gap-[7px] px-3.5 pt-3">
        {live.map((id) => {
          const active = activeIds.has(id);
          return (
            <button
              key={id}
              type="button"
              onClick={() => onToggle(id)}
              aria-pressed={active}
              className={`inline-flex items-center gap-1.5 rounded-full border px-[11px] py-1.5 text-xs transition-colors ${
                active
                  ? "border-orange-primary bg-light-orange font-bold text-bc-accent-text"
                  : "border-gray-200 bg-white font-semibold text-gray-700 hover:bg-orange-50"
              }`}
            >
              <span>{filterLabel(id, thresholdDays)}</span>
              <span className={active ? "font-semibold opacity-60" : "text-gray-400"}>
                {counts[id]}
              </span>
            </button>
          );
        })}
      </div>

      {empty.length > 0 && (
        <div className="flex items-center gap-2 px-3.5 pt-3">
          <span className="flex-shrink-0 text-[11.5px] text-gray-400">Empty:</span>
          {/* Dimmed as a group rather than per chip, so the row reads as one
              de-emphasised cluster instead of six faded buttons. */}
          <div className="flex flex-wrap gap-[7px] opacity-45">
            {empty.map((id) => (
              <button
                key={id}
                type="button"
                onClick={() => onToggle(id)}
                aria-pressed={false}
                className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-white px-2.5 py-[5px] text-[11.5px] font-semibold text-gray-400"
              >
                {filterLabel(id, thresholdDays)} {counts[id]}
              </button>
            ))}
          </div>
        </div>
      )}

      {showThreshold && (
        <div className="mx-3.5 mt-3.5 flex items-center justify-between border-t border-[#F1EEEE] pt-3">
          <span className="text-xs font-semibold text-gray-700">Stale after</span>
          <div className="flex rounded-full bg-gray-100 p-1">
            {THRESHOLD_OPTIONS.map((days) => {
              const active = days === thresholdDays;
              return (
                <button
                  key={days}
                  type="button"
                  onClick={() => onThresholdChange(days)}
                  aria-pressed={active}
                  className={`rounded-full px-[11px] py-1 text-[11.5px] transition-colors ${
                    active
                      ? "bg-white font-bold text-gray-900 shadow-sm ring-1 ring-black/5"
                      : "font-semibold text-gray-400"
                  }`}
                >
                  {days}d
                </button>
              );
            })}
          </div>
        </div>
      )}

      {(showSortNote || onDone) && (
        <div className="mt-3.5 flex items-center justify-between border-t border-[#F1EEEE] bg-[#FBFBFB] px-3.5 py-[11px]">
          {showSortNote ? (
            <span className="text-[11.5px] text-bc-muted">Sorted by longest neglected</span>
          ) : (
            <span />
          )}
          {onDone && (
            <button
              type="button"
              onClick={onDone}
              className="rounded-full bg-orange-primary px-3.5 py-1.5 text-xs font-bold text-white transition-colors hover:bg-[#E2560F]"
            >
              Done
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default FiltersPanel;
